import React from "react";
import { FormIcon } from "./FormIcon";
import { backendUrl } from "../config_prod";
import { FormState } from "../type/form-step";
import {
  mapResumeFields,
  resolveResumeLanguages,
} from "../services/resumePrefillService";
import { isPdfFile } from "../utils/file-validation";

type Props = {
  onContinue: () => void;
  error?: string;
  form: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  onPrefill: (values: Partial<FormState>) => void;
  opportunityId: string;
  onBusy: (busy: boolean) => void;
  t: Record<string, string>;
};

export function ResumePrefillStep({
  form,
  onContinue,
  error,
  update,
  onPrefill,
  opportunityId,
  onBusy,
  t,
}: Props) {
  const [dragging, setDragging] = React.useState(false);
  const fileInputId = React.useId();
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const active = React.useRef<AbortController | null>(null);
  React.useEffect(
    () => () => {
      active.current?.abort();
      onBusy(false);
    },
    [onBusy],
  );

  async function analyze() {
    if (!form.resume || !form.cvAiConsent || busy) return;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    onBusy(true);
    setMessage(t.cvReading);
    try {
      const body = new FormData();
      body.set("file", form.resume);
      body.set("consent", "true");
      body.set("locale", form.lang);
      body.set("opportunity_id", opportunityId);
      const base = `${backendUrl}/kscan/v1/candidate/post/resume-prefill/ats`;
      const response = await fetch(base, {
        method: "POST",
        body,
        signal: controller.signal,
      });
      if (!response.ok) throw new Error("Prefill failed");
      let result = await response.json();
      const token = result.data.uploadId;
      update("cvPrefillToken", token);
      const deadline = Date.now() + 120000;
      while (result.data.status === "processing" && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 1500));
        const poll = await fetch(`${base}/${encodeURIComponent(token)}`, {
          signal: controller.signal,
        });
        if (!poll.ok) throw new Error("Prefill failed");
        result = await poll.json();
      }
      if (result.data.status !== "completed") throw new Error("Prefill failed");
      const values = mapResumeFields(result.data.profile);
      const languages = await resolveResumeLanguages(
        result.data.profile,
        form.lang,
        controller.signal,
      );
      if (languages.length) values.languages = languages;
      if (controller.signal.aborted) return;
      onPrefill(values);
      setMessage(t.cvPrefilled);
      onBusy(false);
      onContinue();
    } catch {
      if (!controller.signal.aborted) setMessage(t.cvFailed);
    } finally {
      if (!controller.signal.aborted) {
        setBusy(false);
        onBusy(false);
      }
    }
  }

  function selectFile(file?: File) {
    if (!file || busy) return;
    if (!isPdfFile(file) || file.size > 5 * 1024 * 1024) {
      setMessage(t.cvInvalid);
      return;
    }
    update("resume", file);
    update("cvPrefillToken", undefined);
    setMessage("");
  }

  return (
    <section
      className="application-step-panel single-step-panel cv-step"
      aria-busy={busy}
    >
      <div className="step-introduction">
        <span className="step-eyebrow">{t.cvEyebrow}</span>
        <h3 className="step-panel-title">{t.cvTitle}</h3>
        <p>{t.cvIntro}</p>
      </div>
      <ol className="cv-prefill-explanation">
        {[
          { label: t.cvUploadStep, icon: "file" as const },
          { label: t.cvExtractStep, icon: "sparkles" as const },
          { label: t.cvReviewStep, icon: "check" as const },
        ].map(({ label, icon }) => (
          <li key={label}>
            <span>
              <FormIcon name={icon} />
            </span>
            {label}
          </li>
        ))}
      </ol>
      <div
        role="group"
        aria-label={t.cvDropTitle}
        className={`cv-dropzone${dragging ? " is-dragging" : ""}${form.resume ? " has-file" : ""}`}
        onDragOver={(event) => {
          event.preventDefault();
          if (!busy) setDragging(true);
        }}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          selectFile(event.dataTransfer.files?.[0]);
        }}
      >
        <span className="cv-upload-icon">
          <FormIcon name={form.resume ? "file" : "upload"} />
        </span>
        {form.resume ? (
          <div className="cv-file-details">
            <strong>{form.resume.name}</strong>
            <span>
              PDF ·{" "}
              {(form.resume.size / 1024 / 1024).toLocaleString(form.lang, {
                maximumFractionDigits: 2,
              })}{" "}
              {form.lang === "fr" ? "Mo" : "MB"}
            </span>
          </div>
        ) : (
          <div className="cv-file-details">
            <strong>{t.cvDropTitle}</strong>
            <span>{t.cvDropHint}</span>
          </div>
        )}
        <label className="button cv-file-picker" htmlFor={fileInputId}>
          {form.resume ? t.cvReplace : t.cvChoose}
          <input
            id={fileInputId}
            className="visually-hidden"
            type="file"
            accept="application/pdf,.pdf"
            disabled={busy}
            onChange={(event) => {
              selectFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
        </label>
        <span className="cv-format-hint">{t.cvFormat}</span>
      </div>
      <div className="cv-consent-card">
        <label className="cv-consent-label">
          <input
            type="checkbox"
            checked={!!form.cvAiConsent}
            disabled={busy}
            onChange={(event) => {
              update("cvAiConsent", event.target.checked);
              if (!event.target.checked) update("cvPrefillToken", undefined);
            }}
          />
          <span>
            <strong>{t.cvConsentTitle}</strong>
            <span>{t.cvConsent}</span>
          </span>
        </label>
        <p className="cv-consent-note">
          <FormIcon name="lock" />
          {t.cvOptional}
        </p>
      </div>
      {error && (
        <p role="alert" className="error cv-feedback">
          {error}
        </p>
      )}
      {message && (
        <div
          role="status"
          className={`cv-feedback ${busy ? "is-reading" : message === t.cvPrefilled ? "is-success" : "is-error"}`}
        >
          {busy ? (
            <span className="verification-spinner" aria-hidden="true" />
          ) : message === t.cvPrefilled ? (
            <FormIcon name="check" />
          ) : null}
          <span>{message}</span>
        </div>
      )}
      <div className="cv-primary-actions">
        <button
          type="button"
          className="button text-button"
          disabled={busy}
          onClick={() => {
            update("cvAiConsent", false);
            update("cvPrefillToken", undefined);
            onContinue();
          }}
        >
          {t.cvManual}
        </button>
        <button
          type="button"
          className="button primary"
          disabled={
            busy ||
            !form.resume ||
            !form.cvAiConsent ||
            message === t.cvPrefilled
          }
          onClick={() => void analyze()}
        >
          <FormIcon name="sparkles" />
          {busy ? t.cvReading : t.cvAnalyze}
        </button>
      </div>
    </section>
  );
}
